/**
 * Documentação IGF — metadados de ficheiro (tamanho/páginas) + prévia PDF.
 * Padrão adaptado de portal-clinico-biblioteca (Gestar): HEAD/Range + pdf.js lazy.
 */
class IgfDocs extends HTMLElement {
  #ready = false;
  #pdfJsPromise = null;
  #sizeCache = new Map();
  #pageCache = new Map();
  #previewState = {
    zoom: 1,
    pdf: null,
    renderToken: 0,
    src: '',
    filename: 'documento.pdf',
  };

  static MIN_ZOOM = 0.5;
  static MAX_ZOOM = 3;
  static ZOOM_STEP = 0.25;

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;

    this.#initMeta();
    this.#initPreview();
  }

  disconnectedCallback() {
    this.#clearPreviewMedia();
  }

  #toAbsoluteUrl(value) {
    try {
      return new URL(value, window.location.href).href;
    } catch (_) {
      return '';
    }
  }

  #formatFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let value = bytes;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const rounded = unitIndex === 0 || value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;

    return `${String(rounded).replace('.', ',')} ${units[unitIndex]}`;
  }

  #formatPages(count) {
    if (!Number.isFinite(count) || count <= 0) return '';
    return count === 1 ? '1 página' : `${count} páginas`;
  }

  async #readContentLength(url) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      try {
        const head = await fetch(url, {
          method: 'HEAD',
          mode: 'cors',
          signal: controller.signal,
        });
        const headLength = Number(head.headers.get('content-length'));

        if (head.ok && Number.isFinite(headLength) && headLength > 0) {
          return headLength;
        }
      } catch (_) {
        /* fallback Range */
      }

      const ranged = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: { Range: 'bytes=0-0' },
        signal: controller.signal,
      });
      const range = ranged.headers.get('content-range');

      if (range && range.includes('/')) {
        const total = Number(range.split('/').pop());
        if (Number.isFinite(total) && total > 0) return total;
      }

      const length = Number(ranged.headers.get('content-length'));
      if (Number.isFinite(length) && length > 1) return length;

      return null;
    } catch (_) {
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async #getFileSize(url) {
    if (this.#sizeCache.has(url)) return this.#sizeCache.get(url);

    const pending = this.#readContentLength(url);
    this.#sizeCache.set(url, pending);
    const bytes = await pending;

    if (!bytes) this.#sizeCache.delete(url);

    return bytes;
  }

  async #ensurePdfJs() {
    if (window.pdfjsLib) return window.pdfjsLib;
    if (this.#pdfJsPromise) return this.#pdfJsPromise;

    const scriptSrc = this.getAttribute('data-pdf-js');
    const workerSrc = this.getAttribute('data-pdf-worker');

    if (!scriptSrc) throw new Error('pdfjs-missing');

    this.#pdfJsPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[data-igf-docs-pdf="${scriptSrc}"]`);

      if (existing) {
        if (existing.dataset.loaded === 'true' && window.pdfjsLib) {
          resolve(window.pdfjsLib);
          return;
        }

        existing.addEventListener('load', () => resolve(window.pdfjsLib));
        existing.addEventListener('error', () => reject(new Error('pdfjs')));
        return;
      }

      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.dataset.igfDocsPdf = scriptSrc;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        if (workerSrc && window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
        }
        resolve(window.pdfjsLib);
      });
      script.addEventListener('error', () => reject(new Error('pdfjs')));
      document.head.append(script);
    });

    return this.#pdfJsPromise;
  }

  async #getPageCount(url) {
    if (this.#pageCache.has(url)) return this.#pageCache.get(url);

    const pending = (async () => {
      const pdfjsLib = await this.#ensurePdfJs();
      const pdf = await pdfjsLib.getDocument({ url }).promise;
      const count = pdf.numPages;
      await pdf.destroy();
      return count;
    })();

    this.#pageCache.set(url, pending);

    try {
      return await pending;
    } catch (_) {
      this.#pageCache.delete(url);
      return null;
    }
  }

  async #fillMeta(node) {
    const url = this.#toAbsoluteUrl(node.getAttribute('data-file-src') || '');
    const typeLabel = node.getAttribute('data-file-type') || 'PDF';
    if (!url.startsWith('http')) {
      node.textContent = typeLabel;
      return;
    }

    const parts = [typeLabel];
    const bytes = await this.#getFileSize(url);

    if (!node.isConnected) return;

    const sizeLabel = this.#formatFileSize(bytes);
    if (sizeLabel) parts.push(sizeLabel);

    node.textContent = parts.join(' · ');
  }

  #initMeta() {
    const nodes = this.querySelectorAll('[data-igf-docs-meta]');
    if (!nodes.length) return;

    const run = (node) => {
      this.#fillMeta(node);
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            obs.unobserve(entry.target);
            run(entry.target);
          }
        },
        { rootMargin: '120px 0px' }
      );

      for (const node of nodes) {
        observer.observe(node);
      }
    } else {
      for (const node of nodes) {
        run(node);
      }
    }
  }

  #clampZoom(value) {
    return Math.min(IgfDocs.MAX_ZOOM, Math.max(IgfDocs.MIN_ZOOM, Math.round(value * 100) / 100));
  }

  #formatZoom(zoom) {
    return `${Math.round(zoom * 100)}%`;
  }

  #getPixelRatio() {
    return Math.min(window.devicePixelRatio || 1, 2);
  }

  #clearPreviewMedia() {
    const media = this.querySelector('[data-igf-docs-preview-media]');
    if (!media) return;

    media.replaceChildren();
  }

  async #renderPdfPages(scroller, pdf, zoom, renderToken) {
    const fitWidth = Math.max(scroller.clientWidth || 280, 280);
    const cssWidth = fitWidth * zoom;
    const pixelRatio = this.#getPixelRatio();
    const pageCount = Math.min(pdf.numPages, 40);
    const scrollRatioY = scroller.scrollHeight ? scroller.scrollTop / scroller.scrollHeight : 0;

    scroller.replaceChildren();

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      if (scroller.dataset.renderToken !== renderToken) return;

      const page = await pdf.getPage(pageNumber);
      const unscaled = page.getViewport({ scale: 1 });
      const cssScale = cssWidth / unscaled.width;
      const viewport = page.getViewport({ scale: cssScale * pixelRatio });
      const canvas = document.createElement('canvas');
      canvas.className = 'igf-docs-preview__page';
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${unscaled.height * cssScale}px`;
      await page.render({
        canvasContext: canvas.getContext('2d'),
        viewport,
      }).promise;

      if (scroller.dataset.renderToken !== renderToken) return;
      scroller.append(canvas);
    }

    scroller.scrollTop = scrollRatioY * scroller.scrollHeight;
  }

  async #renderPdfPreview(media, src, title, zoom) {
    const loading = document.createElement('div');
    loading.className = 'igf-docs-preview__fallback';
    loading.innerHTML = '<p>A carregar prévia…</p>';
    media.append(loading);

    try {
      const pdfjsLib = await this.#ensurePdfJs();
      const pdf = await pdfjsLib.getDocument({ url: src }).promise;
      const scroller = document.createElement('div');
      scroller.className = 'igf-docs-preview__pdf';
      scroller.setAttribute('role', 'document');
      scroller.setAttribute('aria-label', title);
      this.#previewState.renderToken += 1;
      const token = String(this.#previewState.renderToken);
      scroller.dataset.renderToken = token;
      loading.replaceWith(scroller);
      await this.#renderPdfPages(scroller, pdf, zoom, token);
      return pdf;
    } catch (_) {
      const fallback = document.createElement('div');
      fallback.className = 'igf-docs-preview__fallback';
      fallback.innerHTML = `<p>Não foi possível carregar a prévia.</p><a href="${src}" target="_blank" rel="noopener noreferrer">Abrir em nova aba</a>`;
      loading.replaceWith(fallback);
      return null;
    }
  }

  async #downloadFile(url, filename) {
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('download');

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || 'documento.pdf';
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (_) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  #initPreview() {
    const dialogComponent = this.querySelector('dialog-component.igf-docs-preview');
    const dialog = dialogComponent?.querySelector('dialog');
    const media = this.querySelector('[data-igf-docs-preview-media]');
    const download = this.querySelector('[data-igf-docs-preview-download]');
    const zoomControls = this.querySelector('[data-igf-docs-preview-zoom]');
    const zoomLabel = this.querySelector('[data-zoom-label]');
    const zoomIn = this.querySelector('[data-zoom-in]');
    const zoomOut = this.querySelector('[data-zoom-out]');
    const triggers = this.querySelectorAll('[data-igf-docs-preview-open]');

    if (!dialogComponent || !dialog || !media || !download || !triggers.length) return;

    const setZoomUi = () => {
      if (zoomLabel) zoomLabel.textContent = this.#formatZoom(this.#previewState.zoom);
      if (zoomOut) zoomOut.disabled = this.#previewState.zoom <= IgfDocs.MIN_ZOOM;
      if (zoomIn) zoomIn.disabled = this.#previewState.zoom >= IgfDocs.MAX_ZOOM;
    };

    const resetPreview = () => {
      this.#previewState.zoom = 1;
      this.#previewState.pdf = null;
      this.#previewState.renderToken += 1;
      this.#previewState.src = '';
      if (zoomControls) zoomControls.hidden = true;
      setZoomUi();
      this.#clearPreviewMedia();
    };

    const changeZoom = async (nextZoom) => {
      this.#previewState.zoom = this.#clampZoom(nextZoom);
      setZoomUi();
      const scroller = media.querySelector('.igf-docs-preview__pdf');
      if (!scroller || !this.#previewState.pdf) return;

      this.#previewState.renderToken += 1;
      const token = String(this.#previewState.renderToken);
      scroller.dataset.renderToken = token;
      await this.#renderPdfPages(scroller, this.#previewState.pdf, this.#previewState.zoom, token);
    };

    dialog.addEventListener('close', resetPreview);

    zoomIn?.addEventListener('click', () => {
      changeZoom(this.#previewState.zoom + IgfDocs.ZOOM_STEP);
    });

    zoomOut?.addEventListener('click', () => {
      changeZoom(this.#previewState.zoom - IgfDocs.ZOOM_STEP);
    });

    download.addEventListener('click', (event) => {
      event.preventDefault();
      const href = download.getAttribute('href');
      if (!href || href === '#') return;
      this.#downloadFile(href, download.dataset.filename || 'documento.pdf');
    });

    for (const trigger of triggers) {
      trigger.addEventListener('click', async () => {
        const src = trigger.getAttribute('data-preview-src') || '';
        const title = trigger.getAttribute('data-preview-title') || 'Pré-visualização';
        const filename = trigger.getAttribute('data-preview-filename') || 'documento.pdf';

        this.#previewState.zoom = 1;
        this.#previewState.pdf = null;
        this.#previewState.src = src;
        this.#previewState.filename = filename;
        setZoomUi();
        if (zoomControls) zoomControls.hidden = false;

        dialog.setAttribute('aria-label', title);
        download.hidden = !src;
        download.href = src || '#';
        download.dataset.filename = filename;

        dialogComponent.showDialog();
        media.replaceChildren();

        const pdf = await this.#renderPdfPreview(media, src, title, this.#previewState.zoom);
        this.#previewState.pdf = pdf;
        if (zoomControls) zoomControls.hidden = !pdf;
      });
    }
  }
}

if (!customElements.get('igf-docs')) {
  customElements.define('igf-docs', IgfDocs);
}
